import { Router } from '@angular/router';
import { getToday } from '@celerofinancas/core-ts';
import { CompanyService } from '@celerofinancas/service-utils';
import { RecordListFilters } from '@celerofinancas/ui-http-services';
import {
  DASHBOARD_FINANCE_DEFAULT_TOKEN,
  FINANCIAL_STATISTIC,
  PRESENTATION_METHOD,
} from '@celerofinancas/ui-reports';
import { endOfMonth, format, startOfMonth } from 'date-fns';

/**
 * Datas "esse mês" — espelham os valueCards da lib ui-reports (card 86e1mcjz8, design §1.1).
 * Lidas no fuso local: o `Dates.toShortISO` do core-ts devolve a data em UTC, que a partir das 21h
 * de Brasília já é o dia seguinte (card 86e3h0f06).
 */
const firstDayOfMonth = (): string => format(startOfMonth(new Date()), 'yyyy-MM-dd');
const lastDayOfCurrentMonth = (): string => format(endOfMonth(new Date()), 'yyyy-MM-dd');

/**
 * Filtros por item de "Entradas" (groupedRevenue), conforme regras do card 86e1mcjz8.
 */
function revenueItems() {
  return [
    {
      title: 'Previstas',
      subtitle: 'até o final do mês',
      type: 'revenue' as const,
      identifier: 'predicted',
      tooltip: 'Valor total de entradas previstas até o último dia deste mês.',
      cashFlowFilters: {
        financial_record_type: 'revenue',
        is_predicted: true,
        date_type: 'due',
        date_range_start: getToday(),
        date_range_end: lastDayOfCurrentMonth(),
      } as RecordListFilters,
    },
    {
      title: 'Realizadas',
      subtitle: 'no mês atual',
      type: 'revenue' as const,
      identifier: 'done',
      tooltip: 'Valor total de entradas recebidas até o momento, no mês atual.',
      cashFlowFilters: {
        financial_record_type: 'revenue',
        is_done: true,
        date_type: 'payment',
        date_range_start: firstDayOfMonth(),
        date_range_end: getToday(),
      } as RecordListFilters,
    },
    {
      title: 'Total',
      subtitle: 'Previsto e Realizado',
      type: 'revenue' as const,
      identifier: 'total',
      tooltip: 'Valores totais recebidos e previstos para receber, até o fim deste mês.',
      cashFlowFilters: {
        financial_record_type: 'revenue',
        date_type: 'due',
        date_range_start: firstDayOfMonth(),
        date_range_end: lastDayOfCurrentMonth(),
      } as RecordListFilters,
    },
  ];
}

/**
 * Filtros por item de "Saídas" (groupedExpense), conforme regras do card 86e1mcjz8.
 */
function expenseItems() {
  return [
    {
      title: 'Previstas',
      subtitle: 'até o final do mês',
      type: 'expense' as const,
      identifier: 'predicted',
      tooltip: 'Valor total de saídas previstas até o último dia deste mês.',
      cashFlowFilters: {
        financial_record_type: 'expense',
        is_predicted: true,
        date_type: 'due',
        date_range_start: getToday(),
        date_range_end: lastDayOfCurrentMonth(),
      } as RecordListFilters,
    },
    {
      title: 'Realizadas',
      subtitle: 'no mês atual',
      type: 'expense' as const,
      identifier: 'done',
      tooltip: 'Valor total de saídas realizadas até o momento, no mês atual.',
      cashFlowFilters: {
        financial_record_type: 'expense',
        is_done: true,
        date_type: 'payment',
        date_range_start: firstDayOfMonth(),
        date_range_end: getToday(),
      } as RecordListFilters,
    },
    {
      title: 'Total',
      subtitle: 'Previsto e Realizado',
      type: 'expense' as const,
      identifier: 'total',
      tooltip: 'Valores totais pagos e previstos para pagar, até o fim deste mês.',
      cashFlowFilters: {
        financial_record_type: 'expense',
        date_type: 'due',
        date_range_start: firstDayOfMonth(),
        date_range_end: lastDayOfCurrentMonth(),
      } as RecordListFilters,
    },
  ];
}

/**
 * Caixa's monthly comparative graph ("Entradas e Saídas") colors, in series
 * order [Revenues, Predicted revenues, Expenses, Predicted expenses].
 * Predicted values use light blue/red instead of the fleet-wide gray (card 86e2dz9dt).
 */
const CAIXA_BAR_GRAPH_COLOR_SCHEME = {
  domain: ['var(--cds-revenue)', '#A0D2FC', 'var(--cds-expense)', '#F0AFAF'],
};

/**
 * 'Previsão de saldo total' tooltip. The ui-reports default announced
 * "próximos 7 e 30 dias" while the card renders "Para 7 dias" and
 * "Para o final do mês" (card 86e32j3yy). Fixed upstream in ui-reports too;
 * this override delivers it to Caixa ahead of the lib bump and can be dropped
 * once @celerofinancas/ui-reports ships the corrected copy.
 */
const PREDICTED_BALANCE_TOOLTIP =
  'Previsão do valor total de saldo nas contas bancárias para os próximos 7 dias e para o final deste mês.';

/**
 * 'Em atraso' card-level tooltip (card 86e3ndvq7). In ui-reports the card has
 * none, since in the core it sits next to Entradas/Saídas; Caixa shows it on
 * its own, so it gets one. Adapted from the prototype copy, which reads
 * "considerando todo o histórico de movimentações": here the card sums only
 * the current month (items copy and the card 86e3h0f06 redirect).
 */
const LATE_TOOLTIP = 'Total de valores em atraso — a receber ou a pagar — do mês atual.';

/**
 * Clones the default statisticProperties and overrides, opt-in for Caixa:
 * - 'Previsão de saldo total' tooltip copy (card 86e32j3yy) and title icon (card 86e3ndvq7);
 * - 'Em atraso' tooltip and title icon (card 86e3ndvq7);
 * - revenue/expense grouped cards with clickable items (cashFlowFilters);
 * - monthly graph predicted colors (card 86e2dz9dt);
 * - whole-card redirects (onClick) from card 86e2dz9dt:
 *   'Em atraso' → Record List with the late and this month filters applied
 *   (card 86e3h0f06),
 *   'Previsão de saldo total' and 'Saldo final de cada dia' → Cash Flow,
 *   'Resultado mensal' → DFM Report.
 * Keeps the remaining default map entries intact (donuts, value cards, etc).
 */
export function buildCaixaDashboardStatisticProperties(
  company: CompanyService,
  router: Router,
): Map<FINANCIAL_STATISTIC, any> {
  const props = new Map(DASHBOARD_FINANCE_DEFAULT_TOKEN.statisticProperties);
  const redirectToRecordList = buildCaixaOnCardRedirect(company, router);

  props.set(FINANCIAL_STATISTIC.groupedRevenue, {
    presentationMethod: PRESENTATION_METHOD.groupedCard,
    items: revenueItems(),
  });
  props.set(FINANCIAL_STATISTIC.groupedExpense, {
    presentationMethod: PRESENTATION_METHOD.groupedCard,
    items: expenseItems(),
  });

  props.set(FINANCIAL_STATISTIC.groupedLate, {
    ...props.get(FINANCIAL_STATISTIC.groupedLate),
    tooltip: LATE_TOOLTIP,
    showTooltipsIconsTitle: true,
    // Card 86e3h0f06: 'late' covers only the current month, so the Record List
    // opens with "Em atraso" and "Este mês" (by due date) applied. The dates
    // are read on click, so a dashboard left open into a new month stays right.
    onClick: () =>
      redirectToRecordList({
        is_late: true,
        date_type: 'due',
        date_range_start: firstDayOfMonth(),
        date_range_end: lastDayOfCurrentMonth(),
      } as RecordListFilters),
  });
  props.set(FINANCIAL_STATISTIC.groupedPredicted, {
    ...props.get(FINANCIAL_STATISTIC.groupedPredicted),
    tooltip: PREDICTED_BALANCE_TOOLTIP,
    // Card 86e3ndvq7: the tooltip moves from the title hover to an icon beside it.
    showTooltipsIconsTitle: true,
    // Card 86e37v532: without this the ui-reports binding [colorFromValue] is
    // null and the card falls back to `.grouped-item.predicted`, which paints
    // every forecast blue — including the ones rendering a D indicator.
    // The color override that wins over that rule lives in src/styles.scss.
    useColorFromValue: true,
    onClick: () => router.navigate(['cash-flow']),
  });
  props.set(FINANCIAL_STATISTIC.lineDailyBalance, {
    ...props.get(FINANCIAL_STATISTIC.lineDailyBalance),
    onClick: () => router.navigate(['cash-flow']),
  });
  props.set(FINANCIAL_STATISTIC.barMonthlyComparative, {
    ...props.get(FINANCIAL_STATISTIC.barMonthlyComparative),
    colorScheme: CAIXA_BAR_GRAPH_COLOR_SCHEME,
    onClick: () => router.navigate(['results/monthly']),
  });

  return props;
}

/**
 * Estratégia de redirect do Caixa: aplica os filtros no estado do CompanyService
 * e navega para a Lista de Registros (rota interna do SPA caixa-cl-iframe).
 *
 * Navega com `queryParams` (filtros + `fromDashboard: true`) além de espelhar o
 * estado via `setFinancialRecordParams`. Isso é necessário porque
 * `BfmPaginatedFinancialListComponent.handlerDefaultRouterParams()` (ui-cash-flow)
 * SEMPRE reseta `financialRecordParams` a partir de `route.queryParams` quando a
 * navegação não carrega esses params (ver `isEmptyFilter`/`handlerDefaultRouterParams`)
 * — navegar só com o estado pré-setado, sem queryParams, seria sobrescrito no mount
 * do StatementComponent. `fromDashboard: true` é o marcador que essa lib já usa para
 * não aplicar o filtro default (`date_type: 'due'` + próximo mês).
 */
export function buildCaixaOnCardRedirect(
  company: CompanyService,
  router: Router,
): (filters: RecordListFilters) => void {
  return (filters: RecordListFilters) => {
    Object.entries(filters).forEach(([key, value]) => {
      company.setFinancialRecordParams(value, key);
    });
    company.applyPreferences();
    router.navigate(['cash-flow/list'], { queryParams: { ...filters, fromDashboard: true } });
  };
}
